import { observer } from 'mobx-react';
import React, { ReactNode, useEffect, useRef, useState } from 'react';

import Async from 'components/Async';

import * as Styled from './styled';

interface InfiniteScrollProps {
	children: ReactNode;
	threshold?: number;
	rootMargin?: string;
	isLoading: boolean;
	onLoadMore: (page: number) => Promise<void>;
	currentPage: number;
	hasMore: boolean;
}

function InfiniteScrollContainer({
	children,
	threshold = 0,
	rootMargin = '75px',
	isLoading,
	onLoadMore,
	currentPage,
	hasMore,
}: InfiniteScrollProps) {
	const observerTarget = useRef<HTMLDivElement>(null);
	const loadingRef = useRef(false);
	const [hasError, setHasError] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			async (entries) => {
				const target = entries[0];

				if (target.isIntersecting && hasMore && !isLoading && !loadingRef.current && !hasError) {
					loadingRef.current = true;

					try {
						await onLoadMore(currentPage + 1);
					} catch (error) {
						setHasError(true);
					} finally {
						loadingRef.current = false;
					}
				}
			},
			{
				threshold,
				rootMargin,
			},
		);

		const currentTarget = observerTarget.current;
		if (currentTarget) {
			observer.observe(currentTarget);
		}

		return () => {
			if (currentTarget) {
				observer.unobserve(currentTarget);
			}
		};
	}, [currentPage, hasMore, isLoading, onLoadMore, threshold, rootMargin, hasError]);

	const showLoader = currentPage !== 1 ? isLoading : false;

	return (
		<Styled.Wrap>
			{children}
			<div ref={observerTarget} />
			{showLoader && (
				<Styled.AsyncWrap>
					<Async loading />
				</Styled.AsyncWrap>
			)}
		</Styled.Wrap>
	);
}

export default observer(InfiniteScrollContainer);
